import type { ReactNode } from "react";

/**
 * The symbols of the bar on top (ADR 0019; Furkan, 03/10/2026: small symbols, as on a phone, instead of
 * words): drawn here, in the colour of the text around them, sharp at any size. They say nothing by
 * themselves: the button that carries one has the name.
 */
function Icon({ children }: { children: ReactNode }) {
  return (
    <svg className="icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

/** Night: the till goes to "Zwart". */
export function MoonIcon() {
  return (
    <Icon>
      <path fill="currentColor" d="M21 13.9A9 9 0 0 1 10.1 3a9 9 0 1 0 10.9 10.9Z" />
    </Icon>
  );
}

/** Day: the till goes to "Wit". */
export function SunIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="4.4" fill="currentColor" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.35 5.35l1.56 1.56M17.09 17.09l1.56 1.56M5.35 18.65l1.56-1.56M17.09 6.91l1.56-1.56"
      />
    </Icon>
  );
}

/** Beheer: a wheel with eight teeth. */
export function GearIcon() {
  return (
    <Icon>
      <path
        fill="currentColor"
        fillRule="evenodd"
        stroke="currentColor"
        strokeWidth="0.9"
        strokeLinejoin="round"
        d="M10.27 4.8 10.49 1.91 13.51 1.91 13.73 4.8 15.87 5.69 18.07 3.8 20.2 5.93 18.31 8.13 19.2 10.27 22.09 10.49 22.09 13.51 19.2 13.73 18.31 15.87 20.2 18.07 18.07 20.2 15.87 18.31 13.73 19.2 13.51 22.09 10.49 22.09 10.27 19.2 8.13 18.31 5.93 20.2 3.8 18.07 5.69 15.87 4.8 13.73 1.91 13.51 1.91 10.49 4.8 10.27 5.69 8.13 3.8 5.93 5.93 3.8 8.13 5.69Z M12 8.6a3.4 3.4 0 1 0 0 6.8a3.4 3.4 0 1 0 0 -6.8Z"
      />
    </Icon>
  );
}

/**
 * The device this till runs on, before its name in the bar (Furkan, 05/10/2026: drawn like the moon and
 * the wheel, where an emoji stood): a screen on its foot for a computer, a hand terminal with its ticket
 * for a Sunmi, a phone for anything else.
 */
export function DeviceIcon({ type }: { type: string }) {
  const line = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  } as const;
  if (type === "pc") {
    return (
      <Icon>
        <rect {...line} x="2.5" y="4" width="19" height="12.5" rx="2" />
        <path {...line} d="M12 16.5v3.5M8 20h8" />
      </Icon>
    );
  }
  if (type === "sunmi") {
    return (
      <Icon>
        <path {...line} d="M8.5 6.5V2.5h7v4" />
        <rect {...line} x="6" y="6.5" width="12" height="15" rx="2" />
        <path {...line} d="M9.5 11h5M9.5 14.5h5" />
      </Icon>
    );
  }
  return (
    <Icon>
      <rect {...line} x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path {...line} d="M11 18h2" />
    </Icon>
  );
}

/** A row that can be taken and moved: six dots. */
export function GripIcon() {
  return (
    <svg className="icon" viewBox="0 0 12 20" width="12" height="20" aria-hidden="true" focusable="false">
      <g fill="currentColor">
        <circle cx="3" cy="4" r="1.5" />
        <circle cx="9" cy="4" r="1.5" />
        <circle cx="3" cy="10" r="1.5" />
        <circle cx="9" cy="10" r="1.5" />
        <circle cx="3" cy="16" r="1.5" />
        <circle cx="9" cy="16" r="1.5" />
      </g>
    </svg>
  );
}
